import type { ReactNode } from 'react';
import { ERROR_MESSAGES } from '@baex/shared';
import type { ErrorCode, TransferLogEntry } from '@baex/shared';
import { CopyButton } from '../../components/ui/CopyButton';
import { HelpTip } from '../../components/ui/HelpTip';
import { formatBytes } from '../../lib/format';
import { useTimeFormat } from '../../lib/timezone';
import {
  NO_DETAIL_TEXT,
  describeEntry,
  isErrorEntry,
  isServerFault,
} from '../../lib/transferLabels';

/**
 * The one field on this panel whose purpose is not self-evident (see the "Row detail panel"
 * section, "The checksum row"). Verbatim from the spec, which the test asserts against.
 */
export const CHECKSUM_HELP =
  "The SHA-256 of the file's bytes, computed by this service when the file was stored. " +
  'A file is deleted as soon as it has been picked up, so this is the only surviving ' +
  'record of what it contained. When someone reports a damaged or unexpected file, ask ' +
  'them to hash their copy and compare: if the digests match, the file was intact here ' +
  'and the problem lies on their side. The same value is given to the client when it ' +
  'uploads, and in the X-Checksum header when it downloads.';

function Row({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 py-0.5">
      {/*
        Not uppercased. These are the field names from the log format, and the spec's own
        wireframe prints them as `requestId`, `fileId`, `sizeBytes` — the capitals turned
        those into REQUESTID, FILEID and SIZEBYTES, which is both harder to read and no
        longer the name an admin is cross-referencing against a bug report.
      */}
      <dt className="flex w-24 shrink-0 items-center gap-1 pt-px text-2xs text-ink-muted">
        {label}
        {help ? <HelpTip label={`What does ${label} mean?`} text={help} /> : null}
      </dt>
      <dd className="min-w-0 flex-1 break-words text-[12px] text-ink">{children}</dd>
    </div>
  );
}

/**
 * Read-only detail panel (see the "Table" section). Nothing here is clickable or submittable —
 * this whole screen has no actions by design.
 */
export function RowDetail({ entry }: { entry: TransferLogEntry }) {
  const time = useTimeFormat();
  const isError = isErrorEntry(entry);
  // A heading has to name the entry even when the FILE column shows an em dash.
  const heading = describeEntry(entry, time.formatTableTime(entry.time));
  const clientSafeMessage =
    entry.code && entry.code in ERROR_MESSAGES ? ERROR_MESSAGES[entry.code as ErrorCode] : null;

  return (
    <section
      aria-label={`Detail — ${heading}`}
      className="rounded border border-hairline bg-surface-sunken px-3 py-2.5"
    >
      <h3 className="mb-1.5 text-[12px] font-semibold text-ink">Detail — {heading}</h3>
      <dl className="divide-y divide-hairline">
        <Row label="requestId">
          <span className="tabular">{entry.requestId}</span>
        </Row>
        <Row label="code">
          <span className="tabular">{entry.code ?? entry.status}</span>
        </Row>
        {isError ? <Row label="message">{clientSafeMessage ?? '—'}</Row> : null}
        {isError ? (
          <Row label="detail">
            {isServerFault(entry) ? (
              <span className="tabular whitespace-pre-wrap">{entry.detail ?? '—'}</span>
            ) : (
              <span className="text-ink-muted">{entry.detail ?? NO_DETAIL_TEXT}</span>
            )}
          </Row>
        ) : null}
        {!isError ? (
          <>
            <Row label="fileId">
              <span className="tabular">{entry.fileId ?? '—'}</span>
            </Row>
            <Row label="sizeBytes">
              <span className="tabular">
                {formatBytes(entry.sizeBytes)}
                {entry.sizeBytes !== null ? ` (${entry.sizeBytes})` : ''}
              </span>
            </Row>
          </>
        ) : null}
        {/* Only entries that refer to a stored file carry one; nothing is rendered otherwise. */}
        {entry.checksum ? (
          <Row label="checksum" help={CHECKSUM_HELP}>
            <span className="flex items-start gap-2">
              <span className="tabular min-w-0 flex-1 break-all">{entry.checksum}</span>
              <CopyButton value={entry.checksum} label={`Copy checksum for ${heading}`} />
            </span>
          </Row>
        ) : null}
        <Row label="owner">
          <span className="tabular">{entry.ownerClientId ?? '—'}</span>
        </Row>
        <Row label="acting">
          <span className="tabular">{entry.actorClientId ?? '—'}</span>
        </Row>
        <Row label="remote ip">
          <span className="tabular">{entry.remoteIp ?? '—'}</span>
        </Row>
        <Row label="duration">
          <span className="tabular">
            {entry.durationMs === null ? '—' : `${entry.durationMs} ms`}
          </span>
        </Row>
        <Row label="time">
          <span className="tabular" title={time.rawTimestampTitle(entry.time)}>
            {time.formatTimestamp(entry.time)}
          </span>
        </Row>
      </dl>
    </section>
  );
}
